import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CATEGORY_KEY,
  getCostTransactionChartKey,
  PRODUCT_CATEGORY_KEY,
  PRODUCT_KEY,
  PRODUCT_V3_DETAIL_KEY,
  PRODUCT_V3_KEY,
  STOCK_HISTORY_KEY,
  STOCK_ID_KEY,
  STOCK_KEY,
  STOCK_STATISTIC_KEY,
  STOCKS_V3_KEY,
  WAREHOUSE_KEY,
  INVENTORY_SETTINGS_KEY,
} from 'common/constants/queryKeys'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import {
  createCategory,
  createProduct,
  createStock,
  createWarehouse,
  deleteCategory,
  deleteProduct,
  deleteStock,
  deleteWarehouse,
  editCategory,
  editProduct,
  editWarehouse,
  updateProductIssue,
  updateStatus,
  updateStatusWarehouse,
  updateStock,
  updateStockQuantity,
  updateStockStatusV3,
  updateTransferWarehouse,
  updateInventorySettings,
  uploadImageProduct,
} from './index'

export const useMutateEditStatusProduct = (pageSize: number) => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(updateStatus, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: PRODUCT_KEY, exact: false })
    },
  })
}

export const useMutateUpdateStockQuantity = (pageSize: number) => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(updateStockQuantity, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: STOCK_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: STOCK_HISTORY_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: STOCKS_V3_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_DETAIL_KEY })
      await qc.invalidateQueries({ queryKey: STOCK_STATISTIC_KEY })
    },
  })
}

export const useMutateUpdateProductIssue = (pageSize: number) => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(updateProductIssue, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: STOCK_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: STOCK_HISTORY_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: STOCKS_V3_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_DETAIL_KEY })
      await qc.invalidateQueries({ queryKey: STOCK_STATISTIC_KEY })
    },
  })
}

export const useMutateUpdateTransferWarehouse = (pageSize: number) => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(updateTransferWarehouse, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: STOCK_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: STOCK_HISTORY_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: STOCKS_V3_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_DETAIL_KEY })
      await qc.invalidateQueries({ queryKey: STOCK_STATISTIC_KEY })
    },
  })
}

export const useMutateEditStatusWarehouse = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const { t } = useTranslation(['common'])

  return useMutation(updateStatusWarehouse, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: WAREHOUSE_KEY })
    },
  })
}

export const useMutateCreateCategory = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(createCategory, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:create.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: CATEGORY_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_CATEGORY_KEY })
    },
  })
}

export const useMutateCreateProduct = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(createProduct, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:create.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: PRODUCT_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: STOCK_STATISTIC_KEY })
    },
  })
}

export const useMutateCreateWarehouse = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(createWarehouse, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:create.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: WAREHOUSE_KEY })
    },
  })
}

export const useMutateCreateStock = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(createStock, {
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: STOCK_KEY })
      await qc.invalidateQueries({ queryKey: STOCK_HISTORY_KEY })
      await qc.invalidateQueries({ queryKey: STOCKS_V3_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_DETAIL_KEY })
      await qc.invalidateQueries({ queryKey: STOCK_STATISTIC_KEY })
      enqueueSnackbar(t('common:create.success'), { variant: 'success' })
    },
  })
}

export const useMutateUpdateStock = () => {
  const qc = useQueryClient()
  const { t } = useTranslation(['common'])
  const { enqueueSnackbar } = useSnackbar()
  return useMutation(updateStock, {
    onSuccess: async (_, variables) => {
      await qc.invalidateQueries({ queryKey: STOCK_KEY })
      await qc.invalidateQueries({ queryKey: STOCK_HISTORY_KEY })
      await qc.invalidateQueries({ queryKey: [...STOCK_ID_KEY, variables.stockId] })
      await qc.invalidateQueries({ queryKey: STOCKS_V3_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_DETAIL_KEY })
      await qc.invalidateQueries({ queryKey: STOCK_STATISTIC_KEY })
      enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
    },
  })
}

export const useMutateEditProduct = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(editProduct, {
    onSuccess: async (_, variables) => {
      enqueueSnackbar(t('common:create.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: PRODUCT_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: [...PRODUCT_V3_DETAIL_KEY, variables.productId] })
    },
  })
}

export const useMutateEditWarehouse = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(editWarehouse, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:create.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: WAREHOUSE_KEY })
    },
  })
}

export const useMutateUploadImage = () => {
  return useMutation(uploadImageProduct, {
    onSuccess: () => {},
  })
}

export const useMutateEditCategory = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(editCategory, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:create.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: CATEGORY_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_CATEGORY_KEY })
    },
  })
}

export const useMutateRemoveCategory = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(deleteCategory, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: CATEGORY_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_CATEGORY_KEY })
    },
  })
}

export const useMutateRemoveProduct = (pageSize: number) => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(deleteProduct, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
      await queryClient.invalidateQueries({ queryKey: PRODUCT_KEY })
      await queryClient.invalidateQueries({ queryKey: PRODUCT_V3_KEY, exact: false })
      await queryClient.invalidateQueries({ queryKey: CATEGORY_KEY })
      await queryClient.invalidateQueries({ queryKey: STOCK_STATISTIC_KEY })
    },
  })
}

export const useMutateRemoveWarehouse = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(deleteWarehouse, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: WAREHOUSE_KEY })
    },
  })
}

export const useMutateRemoveStock = () => {
  const qc = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(deleteStock, {
    onSuccess: async () => {
      enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
      await qc.invalidateQueries({ queryKey: STOCK_KEY })
      await qc.invalidateQueries({ queryKey: STOCKS_V3_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_DETAIL_KEY })
      await qc.invalidateQueries({ queryKey: PRODUCT_V3_KEY, exact: false })
      await qc.invalidateQueries({ queryKey: STOCK_STATISTIC_KEY })
    },
  })
}

export const useMutateUpdateStockStatusV3 = () => {
  const qc = useQueryClient()
  const { t } = useTranslation(['common'])

  return useMutation(
    ({ stockId, is_active }: { stockId: string; is_active: boolean }) =>
      updateStockStatusV3(stockId, { is_active }),
    {
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: STOCKS_V3_KEY })
        await qc.invalidateQueries({ queryKey: PRODUCT_V3_DETAIL_KEY })
      },
    }
  )
}

export const useMutateUpdateInventorySettings = () => {
  const qc = useQueryClient()

  return useMutation(updateInventorySettings, {
    onSuccess: (res) => {
      qc.setQueryData(INVENTORY_SETTINGS_KEY, res.data.data)
    },
  })
}
